/* =============================================================================
   FAMILLE BOURGEOIS — custom.js
   Scripts JS personnalisés chargés en defer après le DOM.
   Chargé via theme.liquid : <script src="{{ 'custom.js' | asset_url }}" defer>
   Dépendances déclarées avant : lenis.min.js, gsap.min.js, ScrollTrigger.min.js
   ============================================================================= */



   
/* -----------------------------------------------------------------------------
   0. LENIS — Smooth scroll + intégration GSAP ScrollTrigger
----------------------------------------------------------------------------- */
(function () {
  var _Lenis = /** @type {any} */ (window).Lenis;
  var _ST    = /** @type {any} */ (window).ScrollTrigger;
  if (!_Lenis) return;

  var lenis = new _Lenis({
    duration: 1,
    easing: function (/** @type {number} */ t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
    smooth: true,
    direction: 'vertical',
    gestureDirection: 'vertical',
    mouseMultiplier: 1,
    smoothTouch: false,
  });

  function raf(/** @type {number} */ time) {
    lenis.raf(time);
    if (_ST) _ST.update();
    requestAnimationFrame(raf);
  }

  requestAnimationFrame(raf);

  /** @type {any} */ (window).__lenis = lenis;
})();


/* -----------------------------------------------------------------------------
   1. HEADER — Classe "header-hovered"
   Ajoute / retire la classe sur #header-component quand la souris entre ou sort
   de la zone de navigation. Utilisée en CSS pour passer les icônes et liens en
   noir sur le header transparent au survol.
   CSS cible : #header-component.header-hovered .mon-element { ... }
----------------------------------------------------------------------------- */
(function () {
  var header = /** @type {HTMLElement} */ (document.getElementById('header-component'));
  if (!header) return;

  header.addEventListener('mouseenter', function () {
    header.classList.add('header-hovered');
  });
  header.addEventListener('mouseleave', function () {
    header.classList.remove('header-hovered');
  });

  // Quand la souris quitte le viewport par le haut (chrome du navigateur),
  // mouseleave sur header ne se déclenche pas — fallback sur document.
  document.addEventListener('mouseleave', function () {
    header.classList.remove('header-hovered');
  });

  // Touch — le tap sur le burger déclenche mouseenter (simulé) mais la fermeture
  // du menu via tap interne ne déclenche pas mouseleave → header-hovered reste collée.
  // On observe l'attribut [open] du <details> du drawer pour forcer le retrait.
  function watchDrawerClose() {
    var details = /** @type {HTMLDetailsElement} */ (document.getElementById('Details-menu-drawer-container'));
    if (!details) return;
    new MutationObserver(function () {
      if (details.open) {
        header.classList.add('header-hovered');
      } else {
        header.classList.remove('header-hovered');
      }
    }).observe(details, { attributes: true, attributeFilter: ['open'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawerClose);
  } else {
    watchDrawerClose();
  }
})();


/* -----------------------------------------------------------------------------
   1b. HEADER — Classe "header-hovered" fixe sur la page 404
   Sur la 404, le header n'a pas de hero image — on force l'état hovered
   en permanence pour que les liens et icônes soient visibles (noir).
----------------------------------------------------------------------------- */
(function () {
  var template = document.getElementById('MainContent')?.dataset.template ?? '';
  if (template !== '404') return;

  var header = /** @type {HTMLElement} */ (document.getElementById('header-component'));
  if (!header) return;

  header.classList.add('header-hovered');

  new MutationObserver(function () {
    if (!header.classList.contains('header-hovered')) {
      header.classList.add('header-hovered');
    }
  }).observe(header, { attributes: true, attributeFilter: ['class'] });
})();


/* -----------------------------------------------------------------------------
   2. HEADER — Classe "nav-at-top" (position zéro / scroll)
   Ajoute la classe quand le scroll est à 0, la retire dès 100 px de défilement.
   Exclue des pages produit (template "product") pour ne pas interférer avec
   le header dédié à ces pages.
   CSS cible : #header-component.nav-at-top .mon-element { ... }
----------------------------------------------------------------------------- */
(function () {
  var template = document.getElementById('MainContent')?.dataset.template ?? '';
  var header = /** @type {HTMLElement} */ (document.getElementById('header-component'));
  if (!header) return;

  // Pages : transparent header (data-template peut valoir "page" ou "page.suffix")
  if (template.startsWith('page.') && template !== 'page.page-fb-brand-book' && template !== 'page.page-fb-logo-et-identite' && template !== 'page.page-fb-br-book-les-vins' && template && /** @type {any} */ (window).__fbHeaderTransparentPage) {
    document.body.classList.add('fb-page-transparent');
    // [transparent] et [sticky] sont déjà posés par Liquid — setAttribute ici en backup
    // au cas où le Liquid ne correspondrait pas (ex: template suffix non détecté).
    header.setAttribute('transparent', 'not-sticky');
    header.setAttribute('sticky', 'scroll-up');

    var atTopTimer = 0;
    function applyAtTop() {
      var atTop = window.scrollY < 10;
      if (window.scrollY >= 100) {
        header.classList.remove('nav-at-top');
      } else if (atTop) {
        header.classList.add('nav-at-top');
      }
      document.body.style.setProperty('--transparent-header-offset-boolean', atTop ? '0' : '1');
    }
    function updateAtTop() {
      clearTimeout(atTopTimer);
      atTopTimer = setTimeout(applyAtTop, 80);
    }

    applyAtTop(); // Immédiat au chargement, sans délai
    // Lenis fire ses propres events scroll en sync avec son rAF —
    // on les préfère aux events natifs pour éviter les doubles déclenchements.
    var lenis = /** @type {any} */ (window).__lenis;
    if (lenis) {
      lenis.on('scroll', updateAtTop);
    } else {
      window.addEventListener('scroll', updateAtTop, { passive: true });
    }
  }
})();


/* -----------------------------------------------------------------------------
   3. GSAP — Animations génériques au scroll (images + textes)
   Deux classes disponibles à poser sur n'importe quelle section / bloc en BO :
   • "js-fb-fadein"  → fade-in décalé sur toutes les images du bloc
   • "js-fb-fadeup"  → fade-in + remontée sur les enfants directs (titres, textes…)
   Désactivé si l'utilisateur préfère les mouvements réduits (prefers-reduced-motion).
   Dépend de gsap.min.js + ScrollTrigger.min.js chargés avant ce fichier.
----------------------------------------------------------------------------- */
(function () {
  var _gsap = /** @type {any} */ (window).gsap;
  var _ST   = /** @type {any} */ (window).ScrollTrigger;
  if (!_gsap || !_ST) return;

  _gsap.registerPlugin(_ST);

  var mm = _gsap.matchMedia();
  mm.add('(prefers-reduced-motion: no-preference)', function () {

    window.addEventListener('load', function () {

      // — Images : fade-in décalé sur chaque <img> du bloc —
      document.querySelectorAll('.js-fb-fadein').forEach(function (section) {
        var imgs = section.querySelectorAll('img');
        if (!imgs.length) return;

        imgs.forEach(function (img, index) {
          _gsap.from(img, {
            autoAlpha: 0,
            y: 24,
            duration: 0.9,
            delay: index * 0.15,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              once: true,
            },
          });
        });
      });

      // — Textes : fade-in + remontée en stagger sur les blocs de contenu —
      // Cible les .text-block et boutons dans le container quelle que soit la profondeur.
      // Compatible avec les deux structures Shopify (section et groupe imbriqué).
      document.querySelectorAll('.js-fb-fadeup').forEach(function (container) {
        var targets = Array.from(
          container.querySelectorAll('.text-block, a[class*="button"]')
        );
        if (!targets.length) return;

        _gsap.from(targets, {
          autoAlpha: 0,
          y: 20,
          duration: 0.65,
          delay: 0.15,
          stagger: 0.12,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: container,
            start: 'top 85%',
            once: true,
          },
        });
      });

      // — Parallax images : les blocs montent à vitesse différente au scroll —
      // Poser "js-fb-parallax" sur la section dans le BO.
      document.querySelectorAll('.js-fb-parallax').forEach(function (section) {
        var imgLeft        = section.querySelector('.fb-exp-hub__img-left-wrapper');
        var imgRightTop    = section.querySelector('.fb-exp-hub__img-right-top-wrapper');
        var imgRightBottom = section.querySelector('.fb-exp-hub__img-right-bottom-wrapper');

        var trigger = {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        };

        // Image gauche : monte lentement (parallax classique)
        if (imgLeft) {
          _gsap.fromTo(imgLeft,
            { y: -60 },
            { y: 60, ease: 'none', scrollTrigger: trigger }
          );
        }

        // Image droite haute : descend (sens inverse de la gauche)
        if (imgRightTop) {
          _gsap.fromTo(imgRightTop,
            { y: 80 },
            { y: -80, ease: 'none', scrollTrigger: trigger }
          );
        }

        // Image droite basse : monte plus vite (décalage de vitesse)
        if (imgRightBottom) {
          _gsap.fromTo(imgRightBottom,
            { y: -40 },
            { y: 100, ease: 'none', scrollTrigger: trigger }
          );
        }
      });

      // — Parallax oenotourisme : 3 images à vitesses décalées —
      // Poser "js-fb-oeno-parallax" sur la section dans le BO.
      document.querySelectorAll('.js-fb-oeno-parallax').forEach(function (section) {
        var imgLeft   = section.querySelector('.fb-oeno__img-wrapper--left');
        var imgCenter = section.querySelector('.fb-oeno__img-wrapper--center');
        var imgRight  = section.querySelector('.fb-oeno__img-wrapper--right');

        var trigger = {
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        };

        // Image gauche : monte doucement
        if (imgLeft) {
          _gsap.fromTo(imgLeft,
            { y: -40 },
            { y: 60, ease: 'none', scrollTrigger: trigger }
          );
        }

        // Image centre : reste quasi fixe (référence visuelle)
        if (imgCenter) {
          _gsap.fromTo(imgCenter,
            { y: 20 },
            { y: -20, ease: 'none', scrollTrigger: trigger }
          );
        }

        // Image droite : descend plus vite (sens inverse)
        if (imgRight) {
          _gsap.fromTo(imgRight,
            { y: -80 },
            { y: 80, ease: 'none', scrollTrigger: trigger }
          );
        }
      });

      // — Services B : slide-up en cascade au scroll —
      // GSAP gère les offsets cascade (desktop) + l'entrée fade/slide (tous écrans).
      // Les règles CSS nth-child translateY peuvent être retirées, GSAP les remplace.
      var cascadeY = [0, 32, 48, 16]; // desktop offsets — miroir des anciens nth-child
      var mmDesktop  = window.matchMedia('(min-width: 990px)');

      document.querySelectorAll('.fb-services-b').forEach(function (container) {
        var items = Array.from(container.querySelectorAll('.fb-services-b__item'));
        if (!items.length) return;

        _ST.create({
          trigger: container,
          start: 'top 70%',
          once: true,
          onEnter: function () {
            items.forEach(function (item, i) {
              var finalY = mmDesktop.matches ? (cascadeY[i] !== undefined ? cascadeY[i] : 0) : 0;

              // fromTo dans la timeline : from state appliqué uniquement au démarrage
              // → pas de jump visible, fade + slide smooth
              var tl = _gsap.timeline({ delay: 1 + i * 0.15 });
              tl.fromTo(item,
                { y: finalY + 30, autoAlpha: 0 },
                { y: finalY, autoAlpha: 1, duration: 0.8, ease: 'power2.out' }
              );
            });
          },
        });
      });

      _ST.refresh();
    });

  });
})();


/* -----------------------------------------------------------------------------
   4. SCROLL LINKS — Smooth scroll + masquage après défilement
   Les liens .fb-scroll-link déclenchent un scroll doux vers l'ancre #cible.
   Le lien se masque automatiquement quand on dépasse 10 % de la hauteur de page
   (évite qu'il reste visible une fois que l'utilisateur a scrollé).
----------------------------------------------------------------------------- */
(function () {
  var links = document.querySelectorAll('.fb-scroll-link');
  if (!links.length) return;

  var threshold = window.innerHeight * 0.1;

  window.addEventListener('scroll', function () {
    var hidden = window.scrollY > threshold;
    links.forEach(function (l) { l.classList.toggle('is-hidden', hidden); });
  }, { passive: true });

  document.addEventListener('click', function (e) {
    var link = /** @type {Element} */ (e.target)?.closest('.fb-scroll-link');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href || !href.startsWith('#')) return;
    var target = document.getElementById(href.slice(1));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth' });
  });
})();


/* -----------------------------------------------------------------------------
   5. PRIX DÉGRESSIFS — Mise en surbrillance du palier actif
   Écoute le sélecteur de quantité de la page produit et met la classe
   .fb-volume-pricing__row--active sur le palier correspondant.
   Fonctionne avec le snippet snippets/fb-volume-pricing.liquid.
----------------------------------------------------------------------------- */
(function () {
  var table = document.querySelector('.fb-volume-pricing');
  if (!table) return;

  var rows = /** @type {NodeListOf<HTMLElement>} */ (table.querySelectorAll('.fb-volume-pricing__row'));
  if (!rows.length) return;

  // Récupère les paliers (min qty) depuis les data attributes des lignes
  var tiers = Array.from(rows).map(function (row) {
    return parseInt(row.dataset.minQty ?? '0', 10);
  });

  function highlightTier(/** @type {number} */ qty) {
    var activeMin = 0;
    tiers.forEach(function (min) {
      if (qty >= min) activeMin = min;
    });
    rows.forEach(function (row) {
      var isActive = parseInt(row.dataset.minQty ?? '0', 10) === activeMin;
      row.classList.toggle('fb-volume-pricing__row--active', isActive);
    });
  }

  // Écoute les changements du quantity input (thème Shopify standard)
  var qtyInput = document.querySelector('input[name="quantity"]');
  if (qtyInput) {
    highlightTier(parseInt(/** @type {HTMLInputElement} */ (qtyInput).value || '1', 10));
    qtyInput.addEventListener('change', function () {
      highlightTier(parseInt(/** @type {HTMLInputElement} */ (qtyInput).value || '1', 10));
    });
  }

  // Écoute aussi les boutons +/- du sélecteur custom du thème
  document.addEventListener('click', function (e) {
    var btn = /** @type {Element} */ (e.target)?.closest('.quantity-plus, .quantity-minus');
    if (!btn) return;
    setTimeout(function () {
      var input = document.querySelector('input[name="quantity"]');
      if (input) highlightTier(parseInt(/** @type {HTMLInputElement} */ (input).value || '1', 10));
    }, 50);
  });
})();
